import { useState, useMemo } from 'react';
import { Icons } from '../Icons';
import { fmt } from '../../utils/formatters';
import { getSwapChain } from '../../utils/costBasisTracking';
import type { Token, Position, Prices } from '../../types';

interface SwapChainVisualizationProps {
  currentToken: Token;
  allTokens: Token[];
  prices: Prices;
  currentPosition?: Position;
}

interface ChainStep {
  position: Position;
  tokenId: string;
  tokenSymbol: string;
  priceAtSwap: number;
  quantity: number;
  valueAtSwap: number;
  originalCostBasis: number;
  performanceVsOrigin: number;
  performanceVsPrevious: number | null;
  isFirst: boolean;
  isLast: boolean;
  isCurrent: boolean;
}

// Mini Sparkline for swap chain visualization
function MiniSparkline({
  startValue,
  endValue,
  color,
  width = 60,
  height = 24
}: {
  startValue: number;
  endValue: number;
  color: string;
  width?: number;
  height?: number;
}) {
  // Generate a simple curved path from start to end
  const points = useMemo(() => {
    const data: number[] = [];
    const change = endValue - startValue;
    const volatility = Math.abs(change) * 0.15 + 0.05;

    // Simple seeded randomization for consistent curves
    const seed = Math.abs(startValue * 1000 + endValue * 100) % 1000;
    const seededRandom = (i: number) => {
      const x = Math.sin(seed + i * 9999) * 10000;
      return x - Math.floor(x);
    };

    for (let i = 0; i < 12; i++) {
      const progress = i / 11;
      const trendComponent = change * progress;
      const randomComponent = (seededRandom(i) - 0.5) * volatility * startValue;
      const value = startValue + trendComponent + randomComponent * (1 - progress * 0.5);
      data.push(value);
    }
    data[data.length - 1] = endValue;
    return data;
  }, [startValue, endValue]);

  const minY = Math.min(...points);
  const maxY = Math.max(...points);
  const range = maxY - minY || 1;

  const pathPoints = points.map((value, index) => {
    const x = (index / (points.length - 1)) * width;
    const y = height - ((value - minY) / range) * height * 0.8 - height * 0.1;
    return `${x},${y}`;
  }).join(' ');

  const fillPath = `M 0,${height} L ${pathPoints.split(' ').map(p => p).join(' L ')} L ${width},${height} Z`;
  const gradientId = `swapSparkGrad-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.4" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <path d={fillPath} fill={`url(#${gradientId})`} />
      <polyline
        points={pathPoints}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * SwapChainVisualization Component
 *
 * Affiche la timeline complète d'une chaîne de swaps depuis l'investissement initial.
 * Design moderne avec sparklines et glass-morphism.
 */
export function SwapChainVisualization({
  currentToken,
  allTokens,
  prices,
  currentPosition
}: SwapChainVisualizationProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredStep, setHoveredStep] = useState<string | null>(null);
  const COLLAPSE_THRESHOLD = 5;

  // Vérifier si le token fait partie d'une chaîne de swaps
  const hasSwapHistory = currentToken.positions.some(
    p => p.is_swap || p.swap_chain_id
  );

  if (!hasSwapHistory) {
    return null;
  }

  // Récupérer la chaîne complète de swaps
  const chainPositions = getSwapChain(
    currentPosition || currentToken.positions[0],
    allTokens
  );

  if (chainPositions.length <= 1) {
    return null;
  }

  // Construire les étapes de la chaîne
  const steps: ChainStep[] = [];
  let previousValue = 0;

  for (let i = 0; i < chainPositions.length; i++) {
    const position = chainPositions[i];
    const isFirst = i === 0;
    const isLast = i === chainPositions.length - 1;

    const token = allTokens.find(t =>
      t.positions.some(p => p.id === position.id)
    );

    if (!token) continue;

    const tokenId = token.id;
    const tokenSymbol = token.displayName || token.id;
    const priceAtSwap = position.price;
    const quantity = position.quantity;
    const valueAtSwap = quantity * priceAtSwap;
    const originalCostBasis = position.original_cost_basis || position.price;
    const totalOriginalCost = originalCostBasis * quantity;

    const performanceVsOrigin = totalOriginalCost > 0
      ? ((valueAtSwap - totalOriginalCost) / totalOriginalCost) * 100
      : 0;

    const performanceVsPrevious = previousValue > 0
      ? ((valueAtSwap - previousValue) / previousValue) * 100
      : null;

    steps.push({
      position,
      tokenId,
      tokenSymbol,
      priceAtSwap,
      quantity,
      valueAtSwap,
      originalCostBasis,
      performanceVsOrigin,
      performanceVsPrevious,
      isFirst,
      isLast,
      isCurrent: isLast
    });

    previousValue = valueAtSwap;
  }

  const shouldCollapse = steps.length > COLLAPSE_THRESHOLD && !isExpanded;
  const displaySteps = shouldCollapse
    ? [...steps.slice(0, 2), ...steps.slice(-2)]
    : steps;

  const hiddenCount = shouldCollapse ? steps.length - 4 : 0;

  const initialInvestment = steps[0]?.originalCostBasis * steps[0]?.quantity || 0;
  const currentValue = steps[steps.length - 1]?.valueAtSwap || 0;
  const totalPerformance = initialInvestment > 0
    ? ((currentValue - initialInvestment) / initialInvestment) * 100
    : 0;

  // Styles
  const s = {
    container: {
      marginTop: '16px',
      padding: '20px',
      background: 'var(--card-bg)',
      borderRadius: '14px',
      border: '1px solid var(--border)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
    } as React.CSSProperties,
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '20px',
      paddingBottom: '16px',
      borderBottom: '1px solid var(--border)',
    } as React.CSSProperties,
    headerTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    } as React.CSSProperties,
    iconContainer: {
      width: '36px',
      height: '36px',
      borderRadius: '10px',
      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#8b5cf6',
    } as React.CSSProperties,
    titleText: {
      fontSize: '15px',
      fontWeight: 600,
      color: 'var(--text)',
    } as React.CSSProperties,
    subtitle: {
      fontSize: '12px',
      color: 'var(--muted)',
      marginTop: '2px',
    } as React.CSSProperties,
    performanceBadge: (isPositive: boolean) => ({
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: 600,
      background: isPositive
        ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)'
        : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(239, 68, 68, 0.05) 100%)',
      color: isPositive ? '#10b981' : '#ef4444',
      border: `1px solid ${isPositive ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
    }) as React.CSSProperties,
    timeline: {
      position: 'relative',
      paddingLeft: '40px',
    } as React.CSSProperties,
    timelineLine: {
      position: 'absolute',
      left: '15px',
      top: '40px',
      bottom: '40px',
      width: '2px',
      background: 'linear-gradient(180deg, var(--border) 0%, rgba(99, 102, 241, 0.3) 50%, var(--border) 100%)',
      borderRadius: '1px',
    } as React.CSSProperties,
    initialInvestment: {
      marginBottom: '20px',
      position: 'relative',
    } as React.CSSProperties,
    initialIcon: {
      position: 'absolute',
      left: '-40px',
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
    } as React.CSSProperties,
    stepCard: (isHovered: boolean, isCurrent: boolean) => ({
      position: 'relative',
      padding: '14px 16px',
      background: isCurrent
        ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)'
        : isHovered
          ? 'rgba(128, 128, 128, 0.08)'
          : 'rgba(128, 128, 128, 0.04)',
      border: isCurrent
        ? '2px solid rgba(99, 102, 241, 0.4)'
        : '1px solid var(--border)',
      borderRadius: '12px',
      transition: 'all 0.2s ease',
      cursor: 'default',
      marginBottom: '8px',
    }) as React.CSSProperties,
    stepIcon: (isCurrent: boolean) => ({
      position: 'absolute',
      left: '-48px',
      top: '14px',
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      background: isCurrent
        ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
        : 'var(--card-bg)',
      border: isCurrent ? 'none' : '2px solid var(--border)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '11px',
      fontWeight: 700,
      color: isCurrent ? 'white' : 'var(--text)',
      boxShadow: isCurrent ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'none',
    }) as React.CSSProperties,
    stepHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '12px',
    } as React.CSSProperties,
    tokenName: {
      fontSize: '14px',
      fontWeight: 600,
      color: 'var(--text)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    } as React.CSSProperties,
    currentBadge: {
      fontSize: '9px',
      padding: '2px 6px',
      borderRadius: '4px',
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      color: 'white',
      fontWeight: 600,
      textTransform: 'uppercase' as const,
    },
    stepDetails: {
      fontSize: '12px',
      color: 'var(--muted)',
      marginTop: '4px',
    } as React.CSSProperties,
    stepValue: {
      fontSize: '15px',
      fontWeight: 600,
      color: 'var(--text)',
      marginTop: '6px',
    } as React.CSSProperties,
    sparklineContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '4px',
    } as React.CSSProperties,
    livePrice: {
      textAlign: 'right',
      padding: '8px 12px',
      borderRadius: '8px',
      background: 'rgba(128, 128, 128, 0.08)',
    } as React.CSSProperties,
    swapArrow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 0',
      fontSize: '11px',
      color: 'var(--muted)',
    } as React.CSSProperties,
    arrowIcon: (isPositive: boolean) => ({
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      background: isPositive
        ? 'rgba(16, 185, 129, 0.15)'
        : 'rgba(239, 68, 68, 0.15)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: isPositive ? '#10b981' : '#ef4444',
    }) as React.CSSProperties,
    collapseButton: {
      padding: '8px 16px',
      background: 'rgba(128, 128, 128, 0.08)',
      border: '1px solid var(--border)',
      borderRadius: '20px',
      cursor: 'pointer',
      fontSize: '12px',
      color: 'var(--muted)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      transition: 'all 0.2s ease',
    } as React.CSSProperties,
    footer: {
      marginTop: '20px',
      paddingTop: '16px',
      borderTop: '1px solid var(--border)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    } as React.CSSProperties,
    footerInfo: {
      fontSize: '12px',
      color: 'var(--muted)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    } as React.CSSProperties,
    footerSummary: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    } as React.CSSProperties,
    summaryValue: (isPositive: boolean) => ({
      fontSize: '14px',
      fontWeight: 600,
      color: isPositive ? '#10b981' : '#ef4444',
    }) as React.CSSProperties,
  };

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerTitle}>
          <div style={s.iconContainer}>
            <Icons.ArrowRightLeft size={18} />
          </div>
          <div>
            <div style={s.titleText}>Chaîne de Swaps</div>
            <div style={s.subtitle}>
              {steps.length} étape{steps.length > 1 ? 's' : ''} depuis l'investissement initial
            </div>
          </div>
        </div>
        <div style={s.performanceBadge(totalPerformance >= 0)}>
          {totalPerformance >= 0 ? '+' : ''}{fmt(totalPerformance, 1)}%
        </div>
      </div>

      {/* Timeline */}
      <div style={s.timeline}>
        {/* Vertical line */}
        <div style={s.timelineLine} />

        {/* Initial Investment */}
        <div style={s.initialInvestment}>
          <div style={s.initialIcon}>💰</div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '2px' }}>
            Investissement initial
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#10b981' }}>
            ${fmt(initialInvestment, 0)}
          </div>
        </div>

        {/* Steps */}
        {displaySteps.map((step, index) => {
          const isCollapsedGap = shouldCollapse && index === 2;

          if (isCollapsedGap) {
            return (
              <div key={`gap-${index}`} style={{ margin: '16px 0', textAlign: 'center' }}>
                <button
                  onClick={() => setIsExpanded(true)}
                  style={s.collapseButton}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'rgba(128, 128, 128, 0.12)';
                    e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'rgba(128, 128, 128, 0.08)';
                    e.currentTarget.style.borderColor = 'var(--border)';
                  }}
                >
                  <Icons.ChevronDown size={14} />
                  {hiddenCount} swap{hiddenCount > 1 ? 's' : ''} intermédiaire{hiddenCount > 1 ? 's' : ''}
                </button>
              </div>
            );
          }

          const currentPrice = prices[step.tokenId]?.usd || step.priceAtSwap;
          const currentTotalValue = step.quantity * currentPrice;
          const livePerformance = step.valueAtSwap > 0
            ? ((currentTotalValue - step.valueAtSwap) / step.valueAtSwap) * 100
            : 0;
          const isHovered = hoveredStep === step.position.id;
          const isPositiveStep = (step.performanceVsPrevious ?? 0) >= 0;

          return (
            <div key={step.position.id}>
              {/* Swap arrow */}
              {!step.isFirst && (
                <div style={s.swapArrow}>
                  <div style={s.arrowIcon(isPositiveStep)}>
                    <Icons.ChevronDown size={12} />
                  </div>
                  {step.performanceVsPrevious !== null && (
                    <span style={{
                      color: isPositiveStep ? '#10b981' : '#ef4444',
                      fontWeight: 600
                    }}>
                      {isPositiveStep ? '+' : ''}{fmt(step.performanceVsPrevious, 1)}%
                    </span>
                  )}
                  <span>Swap</span>
                </div>
              )}

              {/* Step card */}
              <div
                style={s.stepCard(isHovered, step.isCurrent)}
                onMouseEnter={() => setHoveredStep(step.position.id)}
                onMouseLeave={() => setHoveredStep(null)}
              >
                {/* Token icon */}
                <div style={s.stepIcon(step.isCurrent)}>
                  {step.tokenSymbol.substring(0, 2).toUpperCase()}
                </div>

                <div style={s.stepHeader}>
                  {/* Left side - Token info */}
                  <div style={{ flex: 1 }}>
                    <div style={s.tokenName}>
                      {step.tokenSymbol}
                      {step.isCurrent && (
                        <span style={s.currentBadge}>Actuel</span>
                      )}
                    </div>
                    <div style={s.stepDetails}>
                      {fmt(step.quantity, 6)} @ ${fmt(step.priceAtSwap, 2)}
                    </div>
                    <div style={s.stepValue}>
                      ${fmt(step.valueAtSwap, 0)}
                    </div>

                    {/* Performance vs origin */}
                    {!step.isFirst && (
                      <div style={{
                        marginTop: '8px',
                        fontSize: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span style={{ color: 'var(--muted)' }}>vs origine:</span>
                        <span style={{
                          fontWeight: 600,
                          color: step.performanceVsOrigin >= 0 ? '#10b981' : '#ef4444'
                        }}>
                          {step.performanceVsOrigin >= 0 ? '+' : ''}{fmt(step.performanceVsOrigin, 1)}%
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Right side - Sparkline & Live price */}
                  <div style={s.sparklineContainer}>
                    <MiniSparkline
                      startValue={step.originalCostBasis}
                      endValue={step.priceAtSwap}
                      color={step.performanceVsOrigin >= 0 ? '#10b981' : '#ef4444'}
                    />

                    {step.isCurrent && (
                      <div style={s.livePrice}>
                        <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '2px' }}>
                          Prix actuel
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>
                          ${fmt(currentPrice, 2)}
                        </div>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: livePerformance >= 0 ? '#10b981' : '#ef4444'
                        }}>
                          {livePerformance >= 0 ? '+' : ''}{fmt(livePerformance, 1)}%
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Note */}
                {step.position.note && step.position.is_swap && (
                  <div style={{
                    marginTop: '10px',
                    paddingTop: '10px',
                    borderTop: '1px solid var(--border)',
                    fontSize: '11px',
                    color: 'var(--muted)',
                    fontStyle: 'italic',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <Icons.MessageSquare size={12} />
                    {step.position.note}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Collapse button if expanded */}
        {isExpanded && steps.length > COLLAPSE_THRESHOLD && (
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <button
              onClick={() => setIsExpanded(false)}
              style={s.collapseButton}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(128, 128, 128, 0.12)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(128, 128, 128, 0.08)';
              }}
            >
              <Icons.ChevronUp size={14} />
              Réduire
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={s.footer}>
        <div style={s.footerInfo}>
          <Icons.ExternalLink size={12} />
          <span>{steps.length} swap{steps.length > 1 ? 's' : ''}</span>
          <span style={{ opacity: 0.5 }}>•</span>
          <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>
            {steps[0]?.position.swap_chain_id?.substring(0, 8)}...
          </span>
        </div>
        <div style={s.footerSummary}>
          <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
            ${fmt(initialInvestment, 0)}
          </div>
          <span style={{ color: 'var(--muted)', display: 'flex' }}><Icons.ArrowRight size={14} /></span>
          <div style={s.summaryValue(totalPerformance >= 0)}>
            ${fmt(currentValue, 0)}
          </div>
        </div>
      </div>
    </div>
  );
}
